import React from "react";

import { PickupProfile } from "../../../app/data/manifest/manifestProfiles";
import { STOP_STATUSES } from "../../../app/data/manifest/models";
import { PickupStatus } from "../../../app/data/common/route";
import { formatAddress } from "../../../services/common/address";
import { cn } from "../../../services/common/className";

import Collapsible from "../../../ui-components/collapsible/collapsible";
import Stop from "./stop";
import { ImagingStatusIndicator } from "../imaging/ImagingStatusIndicator";

import "./manifestDetails.scss";
import Tag from "../../../ui-components/molecules/tag/tag";
import { TagColor } from "../../../app/data/common/tagColor";

export interface PickupStopProps {
  pickup: PickupProfile,
} 

 const getPickupInternalStatus = (pickup: PickupProfile): string => {
  // 1) If pickup is completed -> Completed
  if (pickup.pickupStatus === PickupStatus.COMPLETED) {
    return STOP_STATUSES.INTERNAL.COMPLETED;
  }

  // 2) If we have an arrival timestamp -> Active (driver has arrived)
  if (pickup.arrivalTime) {
    return STOP_STATUSES.INTERNAL.INPROGRESS;
  }

  // 3) Default before arrival -> Upcoming
  return STOP_STATUSES.INTERNAL.UPCOMING;
};


// Color of the Tag based on internal status
const statusColor = (internalStatus: string): TagColor => {
  switch (internalStatus) {
    case STOP_STATUSES.INTERNAL.COMPLETED:
      return TagColor.GREEN;
    case STOP_STATUSES.INTERNAL.INPROGRESS:
      return TagColor.YELLOW;
    case STOP_STATUSES.INTERNAL.UPCOMING:
    default:
      return TagColor.GREY;
  }
};

// CSS class used in manifestDetails.scss for styling the collapsible
const statusCssClass = (internalStatus: string) => {
  if (internalStatus === STOP_STATUSES.INTERNAL.COMPLETED) return "complete";
  if (internalStatus === STOP_STATUSES.INTERNAL.INPROGRESS) return "in-progress";
  return "upcoming";
};

const PickupStop: React.FC<PickupStopProps> = ({ pickup }) => {
  const internalStatus = getPickupInternalStatus(pickup);
  const packageTypeColor = (packageType: string): TagColor => {
    switch (packageType) {
      case "ROLL": return TagColor.BLUE;

      case "PALLET": return TagColor.PURPLE;

      default: return TagColor.GREY;
    }
  }  

  return (
    <Stop
      name="Pickup"
      address={formatAddress(pickup.shipper.address)}
      status={internalStatus}
      mods={{ color: statusColor(internalStatus) }} 
    >
      <Collapsible
        key={pickup.pickupNumber}
        mix={cn("manifest-details__stop__collapsible")({
          status: statusCssClass(internalStatus),
        })}
        header={(
          <div className="manifest-details__stop__card-content">
            <h4 className="manifest-details__stop__card-name">{pickup.shipper.name}</h4>
            <div className="manifest-details__stop__card-info">
              <span className="manifest-details__stop__card-info-item">{pickup.pickupNumber}</span>
              {!!pickup.probillNumber && (<span className="manifest-details__stop__card-info-item">
                <a href={"/shipments/" + pickup.probillNumber} target="_blank" rel="noopener noreferrer"
                  className="probill-number">{pickup.probillNumber}</a>
              </span>)}
              <span className="manifest-details__stop__card-info-item">Items: <b>{pickup.items.length}</b></span>
              {pickup.imagingVisibility && (
                <ImagingStatusIndicator
                  status={pickup.imagingVisibility.status}
                  missingDocuments={pickup.imagingVisibility.missingDocuments}
                  completeTooltip="BOL Confirmed"
                  completeAlt="BOL Confirmed"
                />
              )}
            </div>
          </div>         
        )}
      >
        <div className="manifest-details__stop__list">
          {pickup.items.map(item => (
            <div className="manifest-details__stop__list-item">
              <span className="manifest-details__stop__card-info-item">
                <Tag mods={{ color: packageTypeColor(item.packageType) }}>{item.packageType}</Tag>
              </span>
              <span className="manifest-details__stop__card-info-item">
                Units: <b>{item.units}</b>
              </span>
              <span className="manifest-details__stop__card-info-item">
                Weight: <b>{item.weight} lb</b>
              </span>
              {!!item.ratingClass && <span className="manifest-details__stop__card-info-item">
                Class: <b>{item.ratingClass}</b>
              </span>}
            </div>
          ))}
        </div>
      </Collapsible>
    </Stop>
  )
}

export default PickupStop;